import BaseController from "../BaseController";
import Filter from "sap/ui/model/Filter";
import JSONModel from "sap/ui/model/json/JSONModel";
import Sorter from "sap/ui/model/Sorter";
import type Table from "sap/ui/table/Table";
import type ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";

/**
 * Definicja jednej karty statystyk nad tabelą list-page'a. Te same karty co KPI na
 * Dashboardzie (fragment StatCardsRow + cards:NumericHeader).
 * `state` to sap.m.ValueColor — None/Good/Error/Critical/Neutral (NIE sap.ui.core.ValueState).
 * `filter` to raw OData $filter (custom .NET enumy nie przechodzą przez Filter object);
 * funkcja gdy klauzula jest dynamiczna (np. okno czasowe liczone przy każdym refresh).
 */
export interface StatCardDef {
	key: string;
	labelKey: string;
	subtitleKey: string;
	state: "None" | "Good" | "Error" | "Critical" | "Neutral";
	filter?: string | (() => string);
}

/**
 * Reusable helpers for views that render a sap.ui.table.Table bound to OData v4 with
 * search / filter / sort controls (Organizations, Orders, Products, etc.).
 *
 * Subclasses keep their own state model (filters specific to the entity) and call these
 * helpers to apply changes to the binding. Intentionally minimal — we only extract the
 * boilerplate that was duplicated, not the domain-specific filter shape.
 *
 * @namespace computec.b2b.portal.controller.mixins
 */
export default abstract class ListPageController extends BaseController {
	protected getTableBinding(tableId: string): ODataListBinding | undefined {
		const table = this.byId(tableId) as Table | undefined;
		return table?.getBinding("rows") as ODataListBinding | undefined;
	}

	/**
	 * Apply an array of filters to the table binding (joined with AND). Empty array clears.
	 */
	protected applyTableFilters(tableId: string, filters: Filter[]): void {
		const binding = this.getTableBinding(tableId);
		if (!binding) {
			return;
		}
		binding.filter(filters.length > 0 ? new Filter({ filters, and: true }) : []);
	}

	/**
	 * Sort the table by a single field. `key` is "FieldName asc" or "FieldName desc".
	 */
	protected applyTableSortByKey(tableId: string, key: string): void {
		const [field, dir] = key.split(" ");
		if (!field) {
			return;
		}
		this.getTableBinding(tableId)?.sort(new Sorter(field, dir === "desc"));
	}

	/**
	 * Refresh table rows (re-query OData).
	 */
	protected refreshTable(tableId: string): void {
		this.getTableBinding(tableId)?.refresh();
	}

	// === Stats cards (fragment StatCardsRow nad tabelą) ===

	private _statsEntitySet?: string;
	private _statsDefs?: readonly StatCardDef[];

	/**
	 * Inicjalizuje model "stats" z kartami i odpala pierwszy fetch liczników.
	 * Wołaj raz w onInit (void this.initStatsCards(...)); potem refreshStatsCards()
	 * przy każdym wejściu na route / ręcznym odświeżeniu.
	 */
	protected async initStatsCards(entitySet: string, defs: readonly StatCardDef[]): Promise<void> {
		this._statsEntitySet = entitySet;
		this._statsDefs = defs;
		const bundle = await this.getResourceBundle();
		const cards = defs.map(d => ({
			key: d.key,
			label: bundle.getText(d.labelKey) ?? d.key,
			subtitle: bundle.getText(d.subtitleKey) ?? "",
			// NumericHeader.number jest typu string — placeholder do czasu fetcha.
			value: "—",
			state: d.state
		}));
		this.getView().setModel(new JSONModel({ cards }), "stats");
		await this.refreshStatsCards();
	}

	/**
	 * Pobiera liczniki przez /odata/v4/{EntitySet}/$count (najlżejsze możliwe zapytanie —
	 * sam integer, bez wierszy) i wpisuje do modelu "stats". Liczniki są globalne
	 * (nie reagują na filtry tabeli) — celowo, jak na Dashboardzie.
	 */
	protected async refreshStatsCards(): Promise<void> {
		const defs = this._statsDefs;
		const entitySet = this._statsEntitySet;
		const model = this.getView().getModel("stats") as JSONModel | undefined;
		if (!defs || !entitySet || !model) {
			return;
		}
		const counts = await Promise.all(defs.map(d => {
			const filter = typeof d.filter === "function" ? d.filter() : d.filter;
			return this.fetchStatsCount(entitySet, filter);
		}));
		counts.forEach((value, idx) => {
			if (value !== null) {
				model.setProperty(`/cards/${idx}/value`, String(value));
			}
		});
	}

	private async fetchStatsCount(entitySet: string, filter?: string): Promise<number | null> {
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const filterPart = filter ? `?$filter=${encodeURIComponent(filter)}` : "";
			const r = await fetcher.fetch(`/odata/v4/${entitySet}/$count${filterPart}`, {
				headers: { Accept: "text/plain" }
			});
			if (!r.ok) {
				return null;
			}
			const n = Number((await r.text()).trim());
			return Number.isFinite(n) ? n : null;
		} catch {
			return null;
		}
	}
}
